import { CONSTANTS } from "@/constants";
import { Locale } from "@/enums/common.enum";
import {
  ListingType,
  PropertyCompletionStatusType,
  PropertyListingType,
  PropertyPurposeType,
} from "@/enums/property.enum";
import { Property } from "@/interfaces/property.interface";
import { startCase } from "lodash";

export const makePropertySlug = (text: string): string => {
  const slug = text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\u0621-\u064A0-9-]+/g, "")
    .replace(/\-\-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");

  if (slug.length === 0 || !slug) {
    console.error("Failed to get create slug from text", text);
  }

  return slug;
};

export const makeImageUrl = (id: string) => {
  if (id.startsWith("http")) {
    return id;
  }

  return `${process.env.NEXT_PUBLIC_MEDIA_URL}/${id}`;
};

export const makePropertyLink = (
  property: Property,
  locale: Locale,
  referrer?: string,
  addHost = false
): string => {
  let baseLink = `/property/${makePropertySlug(
    property.description[locale].title
  )}/${property.id}`;

  if (referrer) {
    baseLink += `?referrer=${referrer}`;
  }

  if (addHost) {
    return `${CONSTANTS.APP_URL}/${locale}${baseLink}`;
  }

  return baseLink;
};

export const makeYoutubeEmbeddedLink = (url: string) => {
  const splitted = url.split("/");
  const length = splitted.length;

  return `https://youtube.com/embed/${splitted[length - 1]}`;
};

export const makeWhatsappLink = (
  property: Property,
  inquiryId: string,
  locale: Locale
) => {
  let agentReferenceNumber = property.referenceNumber;

  if (!CONSTANTS.IS_PRODUCTION) {
    agentReferenceNumber += "-stg";
  }

  return `https://wa.me/971505506100?text=Hi Sakani! I found this property on your platform and I would like to know more about it: %0a%0a
Inquiry Id: ${inquiryId}
Agent Reference Number: ${agentReferenceNumber} %0a
Purpose: ${startCase(property.listingType)} %0a
Type: ${startCase(property.propertyType)} %0a
Price: ${property.price}  %0a
Link: https://sakanihomes.com/en${makePropertyLink(property, locale)} %0a%0a
Any changes made to this message will result in the inquiry not being sent to the agent.`;
};

export const getZoomLevelFromSlug = (fullSlug: string) => {
  const segments = fullSlug.split("/").filter(Boolean);

  switch (segments.length) {
    case 1: // City level (e.g., "dubai")
      return 11;
    case 2: // District level (e.g., "dubai/marina")
      return 13;
    case 3: // Sub-district/community level (e.g., "dubai/damac-hills-2/dubai-xyz")
      return 16;
    default:
      return 10; // Default zoom level for unknown depth
  }
};

export const getCompatibleListingType = (
  listingType: ListingType,
  genericType: PropertyPurposeType,
  completionStatus?: PropertyCompletionStatusType | null
): PropertyListingType => {
  if (completionStatus === PropertyCompletionStatusType.OFF_PLAN) {
    return PropertyListingType.OFF_PLAN;
  }
  if (genericType === PropertyPurposeType.COMMERCIAL) {
    return listingType === ListingType.BUY
      ? PropertyListingType.COMMERCIAL_BUY
      : PropertyListingType.COMMERCIAL_RENT;
  }
  return listingType === ListingType.BUY
    ? PropertyListingType.BUY
    : PropertyListingType.RENT;
};
